import React, { memo, useContext, useRef, useMemo, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import ADND from '@aytm/lib/adnd/ADND'

import { DataContext } from '@aytm/app/stats/page/sidebar/coding/CodingPanel'

import { SENTIMENTS_ICON_HASH } from '@aytm/app/stats/page/comments/lib/constants'

import useSingleAndDoubleClick from '@aytm/app/stats/hooks/useSingleAndDoubleClick'

import TagSettingsSentiment from '@aytm/app/stats/page/sidebar/coding/components/TagSettingsSentiment'

import style from '@aytm/app/stats/page/sidebar/coding/styles/CodingPanel.module.scss'

const CodingTagSentiment = memo(
  ({ tag, error, settingsItem, isProcessing, isCurrentProcessing, onDNDStart, onDNDEnd }) => {
    const { mode, features, windowUID, isSentimentsDisabled, onTagConfirm } = useContext(DataContext)

    const dndItemRef = useRef(null)

    const isOpened = useMemo(
      () =>
        (settingsItem &&
          settingsItem.item == tag.tag &&
          settingsItem.type == tag.type &&
          settingsItem.codebook === tag.codebook) ||
        false,
      [tag, settingsItem]
    )

    const isDNDDisabled = useMemo(
      () =>
        !['comments', 'video-responses'].includes(mode) ||
        isSentimentsDisabled ||
        features.oe_sentiments_management.disabled,
      [mode, isSentimentsDisabled, features.oe_sentiments_management.disabled]
    )

    const fetchData = useCallback(() => {
      return { ...tag, windowUID }
    }, [tag, windowUID])

    const onActionConfirm = useCallback(action => onTagConfirm(action, tag), [tag, onTagConfirm])

    const dndItem =
      isProcessing || isDNDDisabled
        ? null
        : ADND.sources
            .data({
              type: 'sidebar-tag-sentiment',
              clearDragImage: false
            })
            .onStart(onDNDStart)
            .onEnd(onDNDEnd)
            .fetchData(fetchData)

    const addCommentsSearchOption = useCallback(
      () => dispatchEvent(new CustomEvent('addCommentsSearchOption', { detail: { item: tag } })),
      [tag]
    )

    const onTitleClick = useSingleAndDoubleClick(null, isProcessing ? null : addCommentsSearchOption)

    useEffect(() => {
      if (dndItem) dndItem.connect(dndItemRef.current)

      return () => {
        if (dndItem) dndItem.disconnect()
      }
    }, [dndItem])

    return (
      <>
        <div className={style['codebook-item']} type={tag.type}>
          <div
            ref={dndItemRef}
            className={classNames(style['codebook-subitem'], {
              [style['draggable']]: !isProcessing && !isDNDDisabled
            })}
          >
            <span className={style['codebook-item-icon']}>
              <i className={`icon-aytmfd-sentiment-${SENTIMENTS_ICON_HASH[tag.value]}`} style={{ color: tag.color }} />
            </span>
            <div className={style['codebook-item-tag']}>
              <span
                onClick={['comments', 'video-responses'].includes(mode) ? onTitleClick : null}
                dangerouslySetInnerHTML={{ __html: tag.label }}
              />
            </div>
          </div>
          <div className={style['codebook-item-count']}>{tag.amount}</div>
          {features.oe_sentiments_management.disabled ? null : (
            <TagSettingsSentiment
              tagRef={dndItemRef}
              tag={tag}
              opened={isOpened}
              isProcessing={isProcessing}
              isCurrentProcessing={isCurrentProcessing}
              onConfirm={onActionConfirm}
            />
          )}
        </div>

        {error && error.message && tag.codebook == error.codebook && tag.tag == error.tag && (
          <div className={style['component-error']}>{error.message}</div>
        )}
      </>
    )
  }
)

CodingTagSentiment.propTypes = {
  tag: PropTypes.object,
  error: PropTypes.object,
  settingsItem: PropTypes.object,
  isProcessing: PropTypes.bool,
  isCurrentProcessing: PropTypes.bool,
  onDNDStart: PropTypes.func,
  onDNDEnd: PropTypes.func,
  setDndDroppedTag: PropTypes.func
}

CodingTagSentiment.displayName = 'CodingTagSentiment'

export default CodingTagSentiment
